import { createCriteriaParameters, standardDeleteQuery, standardGetQuery, standardPostQuery, standardPutQuery } from "./apiUtils.js";

import { replacePlaceholdersWithObjValues, setDeleteArrayState, setInsertArrayState } from "../shared/miscUtils.js";

import { SNACKBAR_SUCCESS_CREATED, SNACKBAR_SUCCESS_DELETED, SNACKBAR_SUCCESS_UPDATED } from "../../config/language/english.js";
import * as Constants from "../../config/constants/Constants.js";

// -------
// Transcript translations
// -------

/**
 * Function for querying translations with filters, pagination, sorting etc.
 */
export const queryTextTranslations = (props, options = {}) => {
    const path = Constants.API_TEXT_TRANSLATIONS + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        const states = props.backend.textTranslations;

        setInsertArrayState(states.setData, response.body.elements);
        states.setQueryLoading(false);

        if (options.pagination === false) return;

        if (options.page !== undefined) states.setQueriedUntil(options.page);
        if (response?.body?.total !== undefined) states.setRowCount(response.body.total);
    });
};

/**
 * Function for querying translations with filters, pagination, sorting etc.
 * NB! This is only meant for translation selection pop-up.
 */
export const querySelectTextTranslations = (props, states = {}, options = {}) => {
    const params = createCriteriaParameters(options);
    const path = Constants.API_TEXT_TRANSLATIONS + params;

    standardGetQuery(props, path, (response) => {
        setInsertArrayState(states.setData, response.body.elements);
    });
};

/**
 * Function for querying an existing translation.
 */
export const queryTextTranslation = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_TEXT_TRANSLATIONS_ID, { translation_id: options.id });

    standardGetQuery(props, path, (response) => {
        const states = props.backend.textTranslations;

        setInsertArrayState(states.setData, [{ ...response.body }]);
    });
};

/**
 * Function for creating a new translation.
 */
export const createTextTranslation = (props, options = {}) => {
    const path = Constants.API_TEXT_TRANSLATIONS;

    standardPostQuery(props, path, options.body, (response) => {
        const states = props.backend.textTranslations;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        states.setRowCount((prev) => prev + 1);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_CREATED });
    });
};

/**
 * Function for updating a translation.
 */
export const updateTextTranslation = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_TEXT_TRANSLATIONS_ID, { translation_id: options.id });

    standardPutQuery(props, path, options.body, (response) => {
        const states = props.backend.textTranslations;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
        if (callback) callback(response);
    });
};

/**
 * Function for approving a translation.
 */
export const approveTextTranslations = (props, options = {}) => {
    standardPutQuery(props, Constants.API_TEXT_TRANSLATIONS_APPROVE, options.body, (response) => {
        const states = props.backend.textTranslations;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying a translation.
 */
export const retryTextTranslations = (props, options = {}) => {
    standardPutQuery(props, Constants.API_TEXT_TRANSLATIONS_RETRY, options.body, (response) => {
        const states = props.backend.textTranslations;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for deleting translations.
 */
export const deleteTextTranslations = (props, options = {}) => {
    const path = Constants.API_TEXT_TRANSLATIONS;

    standardDeleteQuery(props, path, options.body, (response) => {
        const states = props.backend.textTranslations;

        setDeleteArrayState(states.setData, options.ids);

        states.setRowCount(states.rowCount - options.ids.length);
        states.setSelected([]);

        // Delete all audio translations related to text translations from states.
        const audioTranslations = props.backend.audioTranslations;
        const audioTranslationIds = audioTranslations.data.filter((a) => options.ids.includes(a.translation_id)).map((a) => a.id);

        setDeleteArrayState(audioTranslations.setData, audioTranslationIds);
        audioTranslations.setRowCount(Math.max(0, audioTranslations.rowCount - audioTranslationIds.length));
        audioTranslations.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};

// -------
// Transcript translation audio
// -------

/**
 * Function for querying translated audio with filters, pagination, sorting etc.
 */
export const queryAudioTranslations = (props, options = {}) => {
    const path = Constants.API_AUDIO_TRANSLATIONS + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        const states = props.backend.audioTranslations;

        setInsertArrayState(states.setData, response.body.elements);
        states.setQueryLoading(false);

        if (options.pagination === false) return;

        if (options.page !== undefined) states.setQueriedUntil(options.page);
        if (response?.body?.total !== undefined) states.setRowCount(response.body.total);
    });
};

/**
 * Function for querying existing translated audio.
 */
export const queryAudioTranslation = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_AUDIO_TRANSLATION_ID, { audio_id: options.id });

    standardGetQuery(props, path, (response) => {
        const states = props.backend.audioTranslations;

        setInsertArrayState(states.setData, [{ ...response.body }]);
    });
};

/**
 * Function for creating a new translated audio request.
 */
export const createAudioTranslation = (props, options = {}) => {
    const path = Constants.API_AUDIO_TRANSLATIONS;

    standardPostQuery(props, path, options.body, (response) => {
        const states = props.backend.audioTranslations;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        states.setRowCount((prev) => prev + 1);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_CREATED });
    });
};

/**
 * Function for updating translated audio.
 */
export const updateAudioTranslation = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_AUDIO_TRANSLATION_ID, { audio_id: options.id });

    standardPutQuery(props, path, options.body, (response) => {
        const states = props.backend.audioTranslations;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
        if (callback) callback(response);
    });
};

/**
 * Function for approving translated audio.
 */
export const approveAudioTranslations = (props, options = {}) => {
    standardPutQuery(props, Constants.API_AUDIO_TRANSLATIONS_APPROVE, options.body, (response) => {
        const states = props.backend.audioTranslations;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying translated audio.
 */
export const retryAudioTranslations = (props, options = {}) => {
    standardPutQuery(props, Constants.API_AUDIO_TRANSLATIONS_RETRY, options.body, (response) => {
        const states = props.backend.audioTranslations;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for deleting translated audio.
 */
export const deleteAudioTranslations = (props, options = {}) => {
    const path = Constants.API_AUDIO_TRANSLATIONS;

    standardDeleteQuery(props, path, options.body, (response) => {
        const states = props.backend.audioTranslations;

        setDeleteArrayState(states.setData, options.ids);

        states.setRowCount(states.rowCount - options.ids.length);
        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};
